import React, { useMemo } from "react";
import { Input, InputNumber, Checkbox, DatePicker, Radio } from "antd";
import Select from "../../../../../../components/generic/Select";

/**
 * Text field renderer
 */
export const TextFieldRenderer = ({ field, disabled, ...props }) => {
  const placeholder = useMemo(
    () => `Enter ${field.label.toLowerCase()}`,
    [field.label],
  );

  return (
    <Input
      placeholder={placeholder}
      disabled={disabled}
      maxLength={field?.max_length || 255}
      {...props}
    />
  );
};

/**
 * Textarea field renderer
 */
export const TextareaFieldRenderer = ({ field, disabled, ...props }) => {
  const placeholder = useMemo(
    () => `Enter ${field.label.toLowerCase()}`,
    [field.label],
  );

  return (
    <Input.TextArea
      placeholder={placeholder}
      disabled={disabled}
      rows={field.rows || 4}
      maxLength={field.max_length}
      showCount={field.max_length ? true : false}
      {...props}
    />
  );
};

/**
 * Email field renderer
 */
export const EmailFieldRenderer = ({ field, disabled, ...props }) => {
  return (
    <Input
      type="email"
      placeholder={`Enter ${field.label.toLowerCase()}`}
      disabled={disabled}
      {...props}
    />
  );
};

/**
 * Phone field renderer
 */
export const PhoneFieldRenderer = ({ field, disabled, ...props }) => {
  return (
    <Input placeholder="e.g. +44 123 456 7890" disabled={disabled} {...props} />
  );
};

/**
 * Number field renderer
 */
export const NumberFieldRenderer = ({ field, disabled, ...props }) => {
  return (
    <InputNumber
      placeholder={`Enter ${field.label.toLowerCase()}`}
      disabled={disabled}
      min={field.min_value}
      max={field.max_value}
      {...props}
      style={{ width: "100%" }}
    />
  );
};

/**
 * Date field renderer
 */
export const DateFieldRenderer = ({ field, disabled, ...props }) => {
  return (
    <DatePicker
      disabled={disabled}
      format="DD/MM/YYYY"
      placeholder="Select date"
      {...props}
      style={{ width: "100%" }}
    />
  );
};

/**
 * Select field renderer
 */
export const SelectFieldRenderer = ({ field, disabled, ...props }) => {
  const options = useMemo(
    () =>
      field.field_options?.map((option) => ({
        label: option.label,
        value: option.value,
      })) || [],
    [field.field_options],
  );

  return (
    <Select
      placeholder={`Select ${field.label.toLowerCase()}`}
      options={options}
      disabled={disabled}
      allowClear={!field.is_required}
      {...props}
    />
  );
};

/**
 * Multi-select field renderer
 */
export const MultiSelectFieldRenderer = ({ field, disabled, ...props }) => {
  const options = useMemo(
    () =>
      field.field_options?.map((option) => ({
        label: option.label,
        value: option.value,
      })) || [],
    [field.field_options],
  );

  return (
    <Select
      mode="multiple"
      placeholder={`Select ${field.label.toLowerCase()}`}
      options={options}
      disabled={disabled}
      maxTagCount="responsive"
      {...props}
    />
  );
};

/**
 * Checkbox field renderer
 */
export const CheckboxFieldRenderer = ({ field, disabled, ...props }) => {
  return (
    <Checkbox disabled={disabled} {...props}>
      {field.checkbox_label || field.label}
    </Checkbox>
  );
};

/**
 * Radio field renderer
 */
export const RadioFieldRenderer = ({ field, disabled, ...props }) => {
  const options = useMemo(
    () =>
      field.field_options?.map((option) => ({
        label: option.label,
        value: option.value,
      })) || [],
    [field.field_options],
  );

  return <Radio.Group options={options} disabled={disabled} {...props} />;
};

/**
 * Field renderer factory
 * Returns the appropriate renderer component based on field type
 */
export const getFieldRenderer = (fieldType) => {
  const renderers = {
    text: TextFieldRenderer,
    textarea: TextareaFieldRenderer,
    email: EmailFieldRenderer,
    phone: PhoneFieldRenderer,
    number: NumberFieldRenderer,
    date: DateFieldRenderer,
    select: SelectFieldRenderer,
    multiselect: MultiSelectFieldRenderer,
    checkbox: CheckboxFieldRenderer,
    radio: RadioFieldRenderer,
    boolean: CheckboxFieldRenderer,
  };

  return renderers[fieldType] || TextFieldRenderer;
};

/**
 * Render field with appropriate component
 */
export const renderField = (field, disabled) => {
  const Renderer = getFieldRenderer(field.field_type);

  return <Renderer field={field} disabled={disabled} />;
};
